import { z } from 'zod'
import { utcDateTimeString } from '../validation'
import { linkedEventSummarySchema, linkedListingSummarySchema } from './plan'
import { voucherDtoSchema } from './voucher'

export const dropStatusValues = ['draft', 'live', 'drawn', 'archived'] as const
export type DropStatus = (typeof dropStatusValues)[number]

/** A draw picks winners once at its draw date; a daily drop gives everyone a try every 24 hours. */
export const dropKindValues = ['draw', 'daily'] as const
export type DropKind = (typeof dropKindValues)[number]

/** Where people can join: only in the app, or also on the website after signing in. */
export const dropParticipationValues = ['app', 'web'] as const
export type DropParticipation = (typeof dropParticipationValues)[number]

export const DAILY_DROP_LIMITS = {
  contingentMax: 10_000,
  winsPerDayMax: 1_000,
  voucherValidDaysMax: 90,
  voucherValidDaysDefault: 14,
  voucherNoteMax: 300,
} as const

/** A daily drop's prizes, and for a signed-in viewer when they can try again or what they won. */
export const dailyDropStateSchema = z.object({
  contingent: z.number().int(),
  remaining: z.number().int(),
  winsPerDay: z.number().int(),
  /** The viewer's next try; null when they can try now, have won, or are not signed in. */
  nextAttemptAt: z.string().nullable(),
  /** The viewer's voucher once they won. */
  voucherId: z.string().uuid().nullable(),
})
export type DailyDropState = z.infer<typeof dailyDropStateSchema>

export const dropEntryStateValues = [
  'none',
  'entered',
  'winner_pending',
  'claimed',
  'forfeited',
  'lost',
] as const
export type DropEntryState = (typeof dropEntryStateValues)[number]

export const dropMinAgeValues = [16, 18] as const
export type DropMinAge = (typeof dropMinAgeValues)[number]

export const dropRuleTypeValues = [
  'interested_in_event',
  'interested_in_listing',
  'invite_friends',
] as const
export type DropRuleType = (typeof dropRuleTypeValues)[number]

/** Per-type rule config; `count` is the threshold for `invite_friends`. */
export const dropRuleConfigSchema = z.object({
  count: z.number().int().min(1).max(100).optional(),
})
export type DropRuleConfig = z.infer<typeof dropRuleConfigSchema>

/** A single eligibility rule as authored by an admin on create/update. */
export const dropEligibilityRuleSchema = z.object({
  type: z.enum(dropRuleTypeValues),
  config: dropRuleConfigSchema.default({}),
})
export type DropEligibilityRuleInput = z.infer<typeof dropEligibilityRuleSchema>

/** A rule enriched with the viewer's live satisfaction for the drop checklist. */
export const dropEligibilityRuleStatusSchema = z.object({
  type: z.enum(dropRuleTypeValues),
  config: dropRuleConfigSchema,
  satisfied: z.boolean(),
  progress: z.object({ have: z.number().int(), need: z.number().int() }).optional(),
})
export type DropEligibilityRuleStatus = z.infer<typeof dropEligibilityRuleStatusSchema>

/** Drop card for the feed splice and the active list; `daily` carries the viewer's state when known. */
export const dropCardDtoSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  heroImageUrl: z.string().nullable(),
  prizeName: z.string(),
  /** Null for a daily drop, which has no draw. */
  drawAt: z.string().nullable(),
  participantCount: z.number().int(),
  minAge: z.number().int(),
  kind: z.enum(dropKindValues).default('draw'),
  participation: z.enum(dropParticipationValues).default('app'),
  daily: dailyDropStateSchema.nullable().default(null),
})
export type DropCardDto = z.infer<typeof dropCardDtoSchema>

export const activeDropsResponseSchema = z.object({
  drops: z.array(dropCardDtoSchema),
})
export type ActiveDropsResponse = z.infer<typeof activeDropsResponseSchema>

/** Drop kinds beyond draws that the client can draw; older apps send none. */
export const activeDropsQuerySchema = z.object({
  include: z.enum(['daily']).optional(),
})
export type ActiveDropsQuery = z.infer<typeof activeDropsQuerySchema>

/** Minimal live-drop teaser embedded on the linked event/listing detail. */
export const dropTeaserDtoSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  prizeName: z.string(),
  /** Null for a daily drop, which has no draw. */
  drawAt: z.string().nullable(),
  participantCount: z.number().int(),
  heroImageUrl: z.string().nullable(),
  kind: z.enum(dropKindValues).default('draw'),
})
export type DropTeaserDto = z.infer<typeof dropTeaserDtoSchema>

export const dropDetailDtoSchema = dropCardDtoSchema.extend({
  description: z.string(),
  prizeDescription: z.string(),
  prizeValueEur: z.string().nullable(),
  winnerCount: z.number().int(),
  claimWindowDays: z.number().int(),
  startsAt: z.string(),
  terms: z.string(),
  status: z.enum(dropStatusValues),
  drawnAt: z.string().nullable(),
  /** Published after the draw as the public audit anchor; null before. */
  drawSeed: z.string().nullable(),
  linkedEventId: z.string().uuid().nullable(),
  linkedListingId: z.string().uuid().nullable(),
  linkedEntityName: z.string().nullable(),
  /** The linked event as a tappable summary for the drop detail, if one is linked. */
  linkedEvent: linkedEventSummarySchema.nullable(),
  /** The linked listing as a tappable summary for the drop detail, if one is linked. */
  linkedListing: linkedListingSummarySchema.nullable(),
  /** All eligibility rules with the viewer's live satisfaction (empty = open to all). */
  eligibilityRules: z.array(dropEligibilityRuleStatusSchema),
  /** Per-viewer state, merged after the anonymous body; 'none' for guests. */
  entryState: z.enum(dropEntryStateValues),
  /** How a daily drop's voucher is redeemed; empty for draws. */
  voucherNote: z.string().default(''),
  /** How many days a daily drop's voucher stays valid; null for draws. */
  voucherValidDays: z.number().int().nullable().default(null),
})
export type DropDetailDto = z.infer<typeof dropDetailDtoSchema>

export const dropDetailResponseSchema = z.object({
  drop: dropDetailDtoSchema,
})
export type DropDetailResponse = z.infer<typeof dropDetailResponseSchema>

export const dropEntryResponseSchema = z.object({
  entryState: z.enum(dropEntryStateValues),
  participantCount: z.number().int(),
})
export type DropEntryResponse = z.infer<typeof dropEntryResponseSchema>

export const dropAttemptResultValues = ['won', 'lost'] as const
export type DropAttemptResult = (typeof dropAttemptResultValues)[number]

export const dropAttemptResponseSchema = z.object({
  result: z.enum(dropAttemptResultValues),
  /** When the viewer can try again; null after a win, which ends their tries. */
  nextAttemptAt: z.string().nullable(),
  remaining: z.number().int(),
  voucher: voucherDtoSchema.nullable(),
})
export type DropAttemptResponse = z.infer<typeof dropAttemptResponseSchema>

export const adminDropDtoSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  heroImageUrl: z.string().nullable(),
  prizeName: z.string(),
  prizeDescription: z.string(),
  prizeValueEur: z.string().nullable(),
  winnerCount: z.number().int(),
  minAge: z.number().int(),
  startsAt: z.string(),
  /** Null for a daily drop, which has no draw. */
  drawAt: z.string().nullable(),
  claimWindowDays: z.number().int(),
  terms: z.string(),
  status: z.enum(dropStatusValues),
  drawSeed: z.string().nullable(),
  drawnAt: z.string().nullable(),
  linkedEventId: z.string().uuid().nullable(),
  linkedListingId: z.string().uuid().nullable(),
  linkedEntityName: z.string().nullable(),
  /** Authored eligibility rules, editable while the drop is a draft. */
  eligibilityRules: z.array(dropEligibilityRuleSchema).default([]),
  participantCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  kind: z.enum(dropKindValues),
  participation: z.enum(dropParticipationValues),
  prizeContingent: z.number().int().nullable(),
  winsPerDay: z.number().int().nullable(),
  voucherValidDays: z.number().int().nullable(),
  voucherNote: z.string(),
  prizesWon: z.number().int(),
})
export type AdminDropDto = z.infer<typeof adminDropDtoSchema>

export const adminDropsResponseSchema = z.object({
  drops: z.array(adminDropDtoSchema),
})
export type AdminDropsResponse = z.infer<typeof adminDropsResponseSchema>

export const adminDropResponseSchema = z.object({
  drop: adminDropDtoSchema,
})
export type AdminDropResponse = z.infer<typeof adminDropResponseSchema>

const priceString = z.string().regex(/^\d{1,8}(\.\d{1,2})?$/)
/** Absent, null, or empty means "no stated value"; the decimal check still applies when present. */
const optionalPriceString = priceString.or(z.literal('')).nullish()

const adminDropFields = {
  title: z.string().min(1).max(200),
  description: z.string().max(5000),
  prizeName: z.string().min(1).max(200),
  prizeDescription: z.string().max(5000),
  prizeValueEur: optionalPriceString,
  winnerCount: z.number().int().min(1).max(100),
  minAge: z.union([z.literal(dropMinAgeValues[0]), z.literal(dropMinAgeValues[1])]),
  startsAt: utcDateTimeString,
  /** Required for draws; daily drops have none. */
  drawAt: utcDateTimeString,
  claimWindowDays: z.number().int().min(1).max(60),
  terms: z.string().min(1).max(20000),
  linkedEventId: z.string().uuid().nullable(),
  linkedListingId: z.string().uuid().nullable(),
  rules: z.array(dropEligibilityRuleSchema).max(10),
  kind: z.enum(dropKindValues),
  participation: z.enum(dropParticipationValues),
  prizeContingent: z.number().int().min(1).max(DAILY_DROP_LIMITS.contingentMax),
  winsPerDay: z.number().int().min(1).max(DAILY_DROP_LIMITS.winsPerDayMax),
  voucherValidDays: z.number().int().min(1).max(DAILY_DROP_LIMITS.voucherValidDaysMax),
  voucherNote: z.string().max(DAILY_DROP_LIMITS.voucherNoteMax),
}

const adminDropCreateFieldsSchema = z.object({
  ...adminDropFields,
  description: adminDropFields.description.default(''),
  prizeDescription: adminDropFields.prizeDescription.default(''),
  winnerCount: adminDropFields.winnerCount.default(1),
  minAge: adminDropFields.minAge.default(18),
  drawAt: adminDropFields.drawAt.optional(),
  claimWindowDays: adminDropFields.claimWindowDays.default(7),
  linkedEventId: adminDropFields.linkedEventId.optional(),
  linkedListingId: adminDropFields.linkedListingId.optional(),
  rules: adminDropFields.rules.optional(),
  kind: adminDropFields.kind.default('draw'),
  participation: adminDropFields.participation.default('app'),
  prizeContingent: adminDropFields.prizeContingent.optional(),
  winsPerDay: adminDropFields.winsPerDay.optional(),
  voucherValidDays: adminDropFields.voucherValidDays.default(
    DAILY_DROP_LIMITS.voucherValidDaysDefault,
  ),
  voucherNote: adminDropFields.voucherNote.default(''),
})

export const adminCreateDropRequestSchema = adminDropCreateFieldsSchema.superRefine((data, ctx) => {
  if (data.kind === 'draw') {
    if (!data.drawAt) {
      ctx.addIssue({ code: 'custom', path: ['drawAt'], message: 'A draw needs its draw date' })
    }
    return
  }
  if (data.prizeContingent === undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['prizeContingent'],
      message: 'A daily drop needs its contingent',
    })
  }
  if (data.winsPerDay === undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['winsPerDay'],
      message: 'A daily drop needs wins per day',
    })
  }
  if (!data.linkedListingId) {
    ctx.addIssue({
      code: 'custom',
      path: ['linkedListingId'],
      message: 'A daily drop needs the place its vouchers are redeemed at',
    })
  }
})
export type AdminCreateDropRequest = z.infer<typeof adminCreateDropRequestSchema>
/** What a client sends to create a drop; the server fills in the defaults. */
export type AdminCreateDropRequestInput = z.input<typeof adminCreateDropRequestSchema>

/** Only the fields the request names: zod's partial() would otherwise fill in the defaults. */
export const adminUpdateDropRequestSchema = z.object(adminDropFields).partial()
export type AdminUpdateDropRequest = z.infer<typeof adminUpdateDropRequestSchema>

/** A daily drop's numbers and winners for the admin panel. */
export const adminDailyDropStatsSchema = z.object({
  /** Tries since 00:00 Vienna. */
  attemptsToday: z.number().int(),
  attemptsTotal: z.number().int(),
  players: z.number().int(),
  prizesWon: z.number().int(),
  remaining: z.number().int(),
  vouchersRedeemed: z.number().int(),
  winners: z.array(
    z.object({
      userId: z.string().uuid(),
      displayName: z.string().nullable(),
      avatarUrl: z.string().nullable(),
      wonAt: z.string(),
      code: z.string(),
      validUntil: z.string(),
      redeemedAt: z.string().nullable(),
    }),
  ),
})
export type AdminDailyDropStats = z.infer<typeof adminDailyDropStatsSchema>

export const adminDropEntryDtoSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  createdAt: z.string(),
  drawRank: z.number().int().nullable(),
  state: z.enum(dropEntryStateValues),
  wonAt: z.string().nullable(),
  notifiedAt: z.string().nullable(),
  remindedAt: z.string().nullable(),
  claimedAt: z.string().nullable(),
  forfeitedAt: z.string().nullable(),
  /** When the admin-composed winner message reached this winner; null until sent. */
  winnerMessageSentAt: z.string().nullable(),
})
export type AdminDropEntryDto = z.infer<typeof adminDropEntryDtoSchema>

export const adminDropEntriesResponseSchema = z.object({
  entries: z.array(adminDropEntryDtoSchema),
  participantCount: z.number().int(),
})
export type AdminDropEntriesResponse = z.infer<typeof adminDropEntriesResponseSchema>

export const dropWinnerMessageRequestSchema = z.object({
  message: z.string().min(1).max(2000),
})
export type DropWinnerMessageRequest = z.infer<typeof dropWinnerMessageRequestSchema>

/** Reference to an already-uploaded chat document (the `/images/dm-file` response shape). */
export const dropWinnerAttachmentSchema = z.object({
  path: z.string().min(1),
  fileName: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(255),
  sizeBytes: z.number().int().min(0),
})
export type DropWinnerAttachment = z.infer<typeof dropWinnerAttachmentSchema>

export const dropWinnersMessageRequestSchema = z.object({
  message: z.string().min(1).max(2000),
  attachment: dropWinnerAttachmentSchema.optional(),
  /** Send again to winners who already received a message (explicit re-send). */
  resend: z.boolean().optional(),
})
export type DropWinnersMessageRequest = z.infer<typeof dropWinnersMessageRequestSchema>

export const dropWinnersMessageResponseSchema = z.object({
  sentCount: z.number().int(),
  skippedCount: z.number().int(),
})
export type DropWinnersMessageResponse = z.infer<typeof dropWinnersMessageResponseSchema>

export const dropHeroUploadResponseSchema = z.object({
  heroImageUrl: z.string(),
})
export type DropHeroUploadResponse = z.infer<typeof dropHeroUploadResponseSchema>
